import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import Avatar from "react-avatar";

interface UsersListProps {
    users: any[];
    query: string;
    isLoading: boolean;
    error: string;
    canViewMetadata: boolean;
    canResetPassword: boolean;
    canEditUser: boolean;
    canSendMessage: boolean;
    canImpersonate: boolean;
    isSuperAdmin: boolean;
    canManageMemberships: boolean;
    onShowDetails: (user: any) => void;
    onResetPassword: (user: any) => void;
    onEdit: (user: any) => void;
    onSendMessage: (user: any) => void;
    onCreateLoginLink: (user: any) => void;
    onManageClaims: (user: any) => void;
    onArchive: (user: any) => void;
    onDelete: (user: any) => void;
}

const ActionButton = ({ icon, label, onClick, className = "border-gray-300 text-gray-700 bg-white hover:bg-gray-50" }) => (
    <button type="button" onClick={onClick} className={`inline-flex items-center px-2.5 py-1.5 border shadow-sm text-xs font-medium rounded transition-colors ${className}`}>
        <FontAwesomeIcon icon={["fad", icon] as any} className="w-3 h-3 mr-1" />
        {label}
    </button>
);

export const UsersList = ({
    users,
    query,
    isLoading,
    error,
    canViewMetadata,
    canResetPassword,
    canEditUser,
    canSendMessage,
    canImpersonate,
    isSuperAdmin,
    canManageMemberships,
    onShowDetails,
    onResetPassword,
    onEdit,
    onSendMessage,
    onCreateLoginLink,
    onManageClaims,
    onArchive,
    onDelete,
}: UsersListProps) => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            {isLoading && (
                <div className="flex items-center justify-center gap-2 py-3 border-b border-gray-200 text-sm text-gray-600" role="status">
                    <FontAwesomeIcon icon={["fad", "spinner"]} className="w-4 h-4 animate-spin" />
                    Searching users…
                </div>
            )}

            {error ? (
                <div className="text-center py-12 text-red-700">
                    <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mx-auto h-10 w-10 text-red-400" />
                    <p className="mt-3 text-sm">{error}</p>
                </div>
            ) : users.length === 0 && !isLoading ? (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={["fad", "users"]} className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                    <p className="mt-1 text-sm text-gray-500">{query ? "Try adjusting your search terms or filters." : "Get started by creating a new user."}</p>
                </div>
            ) : (
                <div className={`divide-y divide-gray-200 ${isLoading ? "opacity-60" : ""}`} aria-busy={isLoading}>
                    {users.map((user) => (
                        <div key={user.id} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start gap-4">
                                <Avatar name={user.fullName} src={user.photo} size="48px" round={true} className="flex-shrink-0 shadow-sm border-2 border-white" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900">{user.fullName || user.email || user.id}</div>
                                    <div className="text-xs text-gray-500 font-mono truncate">{user.id}</div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                                        {user.email && <span><FontAwesomeIcon icon={["fad", "envelope"]} className="w-3 h-3 mr-1.5 text-gray-400" />{user.email}</span>}
                                        {user.phoneNumber && <span><FontAwesomeIcon icon={["fad", "phone"]} className="w-3 h-3 mr-1.5 text-gray-400" />{user.phoneNumber}</span>}
                                        {user.sourceID && <span className="text-xs text-gray-500"><FontAwesomeIcon icon={["fad", "database"]} className="w-3 h-3 mr-1.5 text-gray-400" />{user.sourceID}</span>}
                                    </div>

                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {canViewMetadata && <ActionButton icon="info-circle" label="Details" onClick={() => onShowDetails(user)} />}
                                        {canResetPassword && <ActionButton icon="key" label="Reset Password" onClick={() => onResetPassword(user)} />}
                                        {canEditUser && <ActionButton icon="edit" label="Edit" onClick={() => onEdit(user)} />}
                                        {canSendMessage && <ActionButton icon="message" label="Message" onClick={() => onSendMessage(user)} className="border-green-300 text-green-700 bg-green-50 hover:bg-green-100" />}
                                        {canImpersonate && <ActionButton icon="link" label="Login Link" onClick={() => onCreateLoginLink(user)} className="border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100" />}
                                        {isSuperAdmin && <ActionButton icon="shield-check" label="Claims" onClick={() => onManageClaims(user)} className="border-red-300 text-red-700 bg-red-50 hover:bg-red-100" />}
                                        {canManageMemberships && <ActionButton icon="archive" label="Archive" onClick={() => onArchive(user)} className="border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100" />}
                                        {canManageMemberships && <ActionButton icon="trash" label="Delete" onClick={() => onDelete(user)} className="border-red-300 text-red-700 bg-red-50 hover:bg-red-100" />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    </div>
);
